import * as A from 'astronomy-engine';
import type { Place } from '../astro/frame';
import { bortleFromSqm, skyBrightnessAt } from '../astro/lightpollution';
import { altAzToEnu } from '../lib/math';
import { eyeSkyFor, starsSeen } from '../render/nakedeye';
import { inUK } from './conditions';
import { fetchWithin } from '../lib/net';

// "Where to go tonight": public places darker than home, chosen by how many stars you can expect to see
// there tonight (sky darkness, the Moon and the cloud forecast), listed nearest first, with directions.

export type SiteKind = 'reserve' | 'park' | 'sanctuary' | 'urban' | 'viewpoint' | 'common' | 'country-park' | 'forest';

export interface Site {
  id: string;
  name: string;
  lat: number;
  lon: number;
  kind: SiteKind;
  /** Zenith sky brightness from the light-pollution atlas (mag/arcsec²). */
  sqm: number;
  /** Where to park, when that differs from the point itself, and the walk from there (metres). */
  park?: [number, number];
  walk?: number;
  /** The International Dark Sky Place it lies in. */
  within?: string;
  /** OpenStreetMap element, e.g. "node/123". */
  osm?: string;
  /** Which landmass it is on (sites are only suggested on the one you are on). */
  land?: number;
  /** A viewpoint near a cliff edge. */
  cliff?: boolean;
}

export const KIND_LABEL: Record<SiteKind, string> = {
  reserve: 'Dark Sky Reserve',
  park: 'Dark Sky Park',
  sanctuary: 'Dark Sky Sanctuary',
  urban: 'Urban Night Sky Place',
  viewpoint: 'Viewpoint',
  common: 'Common land',
  'country-park': 'Country park',
  forest: 'Forest car park',
};

export interface HourScore {
  t: number;
  cloud: number | null; // effective cloud %, null if no forecast
  nelm: number; // faintest star overhead on a clear sky, as Naked eye has it
  stars: number; // expected number of stars on view, as Naked eye shows them
  /** False for hours before you could arrive. */
  ok: boolean;
}

export interface SiteResult {
  site: Site;
  /** Distance in a straight line. */
  km: number;
  bearing: number;
  hours: HourScore[];
  /** Mean clear sky across the dark hours (%), null without a forecast. */
  clear: number | null;
  /** Stars on view at the best hour, and when that is. */
  best: HourScore;
  /** Contiguous stretch of good hours around the best one. */
  from: number;
  to: number;
  score: number;
}

export interface Plan {
  window: { start: number; end: number; kind: 'astronomical' | 'nautical' } | null;
  home: { sqm: number; bortle: number; hours: HourScore[]; best: HourScore | null; clear: number | null };
  results: SiteResult[];
  moon: { lit: number; upHours: number };
  verdict: { tone: 'go' | 'stay' | 'cloudy' | 'moon' | 'none'; title: string; text: string };
  forecast: boolean;
  searchedKm: number;
  /** The place the verdict names as the best bet, if any. */
  bestBet: string | null;
}

// ---------- Data ----------
let sitesP: Promise<Site[]> | null = null;
export function loadSites(): Promise<Site[]> {
  sitesP ??= fetch(`${import.meta.env.BASE_URL}data/sites-uk.json`).then((r) => (r.ok ? (r.json() as Promise<Site[]>) : []), () => [] as Site[]);
  return sitesP;
}

/** Landmass outlines (Natural Earth, simplified): trips never cross the sea. */
interface Land { id: number; bbox: [number, number, number, number]; rings: [number, number][][] }
let lands: Land[] | null = null;
let landsP: Promise<Land[]> | null = null;
export function loadLands(): Promise<Land[]> {
  landsP ??= fetch(`${import.meta.env.BASE_URL}data/lands-uk.json`)
    .then((r) => (r.ok ? (r.json() as Promise<Land[]>) : []), () => [] as Land[])
    .then((l) => (lands = l));
  return landsP;
}

function inRing(lon: number, lat: number, ring: [number, number][]) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** The landmass a place is on (allowing about 3 km for simplified coastlines), or null if none we cover. */
export function landOf(p: { lat: number; lon: number }, list = lands): number | null {
  if (!list) return null;
  for (const l of list) {
    const [x0, y0, x1, y1] = l.bbox;
    if (p.lon < x0 || p.lon > x1 || p.lat < y0 || p.lat > y1) continue;
    if (l.rings.some((r) => inRing(p.lon, p.lat, r))) return l.id;
  }
  // Near the coast: the closest outline edge within 3 km (a flat projection is fine at this scale).
  const kx = 111.32 * Math.cos((p.lat * Math.PI) / 180), ky = 110.57;
  let best: number | null = null, bd = 3;
  for (const l of list) {
    const [x0, y0, x1, y1] = l.bbox;
    if (p.lon < x0 - 0.05 || p.lon > x1 + 0.05 || p.lat < y0 - 0.03 || p.lat > y1 + 0.03) continue;
    for (const r of l.rings)
      for (let i = 1; i < r.length; i++) {
        const ax = (r[i - 1][0] - p.lon) * kx, ay = (r[i - 1][1] - p.lat) * ky;
        const bx = (r[i][0] - p.lon) * kx, by = (r[i][1] - p.lat) * ky;
        const dx = bx - ax, dy = by - ay;
        const t = Math.max(0, Math.min(1, -(ax * dx + ay * dy) / (dx * dx + dy * dy || 1)));
        const d = Math.hypot(ax + t * dx, ay + t * dy);
        if (d < bd) {
          bd = d;
          best = l.id;
        }
      }
  }
  return best;
}

// ---------- Geometry ----------
const R = 6371;
export function distanceKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const p1 = (a.lat * Math.PI) / 180, p2 = (b.lat * Math.PI) / 180;
  const dp = p2 - p1, dl = ((b.lon - a.lon) * Math.PI) / 180;
  return 2 * R * Math.asin(Math.sqrt(Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2));
}
export function bearingDeg(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const p1 = (a.lat * Math.PI) / 180, p2 = (b.lat * Math.PI) / 180, dl = ((b.lon - a.lon) * Math.PI) / 180;
  const y = Math.sin(dl) * Math.cos(p2), x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}
/** Rough drive time, for when you could arrive: roads wind about 1.25× the straight line, at around 55 km/h door to door. */
export const estimateDrive = (km: number) => Math.round(5 + km * 1.3);

// ---------- Tonight's dark hours ----------
export function darkWindow(place: Place, now: Date): Plan['window'] {
  const obs = new A.Observer(place.lat, place.lon, place.height);
  const sunAlt = (t: Date) => {
    const e = A.Equator(A.Body.Sun, t, obs, true, true);
    return A.Horizon(t, obs, e.ra, e.dec, 'normal').altitude;
  };
  for (const [alt, kind] of [[-18, 'astronomical'], [-12, 'nautical']] as const) {
    const inDark = sunAlt(now) < alt;
    // Search from a little before now so a window already in progress is found.
    const start = inDark ? now : A.SearchAltitude(A.Body.Sun, obs, -1, now, 1.2, alt)?.date;
    if (!start) continue;
    const end = A.SearchAltitude(A.Body.Sun, obs, +1, start, 1.2, alt)?.date;
    if (!end) continue;
    return { start: start.getTime(), end: end.getTime(), kind };
  }
  return null;
}

// ---------- Forecast ----------
interface PointForecast { t: number[]; cloud: (number | null)[] }

async function forecastFor(points: { lat: number; lon: number }[]): Promise<PointForecast[] | null> {
  if (!points.length) return [];
  const uk = inUK(points[0].lat, points[0].lon);
  const lat = points.map((p) => p.lat.toFixed(2)).join(',');
  const lon = points.map((p) => p.lon.toFixed(2)).join(',');
  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
    '&hourly=cloud_cover,cloud_cover_low,cloud_cover_mid,cloud_cover_high&forecast_days=3&timeformat=unixtime&timezone=GMT' +
    (uk ? '&models=ukmo_seamless' : '');
  try {
    const j = await fetchWithin(url, (r) => (r.ok ? r.json() : null));
    if (!j) return null;
    const arr = (Array.isArray(j) ? j : [j]) as { hourly: Record<string, (number | null)[]> & { time: number[] } }[];
    return arr.map(({ hourly: h }) => ({
      t: h.time.map((x) => x * 1000),
      cloud: h.time.map((_, i) => {
        const low = h.cloud_cover_low[i], mid = h.cloud_cover_mid[i], high = h.cloud_cover_high[i], tot = h.cloud_cover[i];
        if (low == null && mid == null && high == null) return tot;
        // Thin high cloud dims the sky but still lets the brighter stars through.
        return Math.max(low ?? 0, mid ?? 0, 0.5 * (high ?? 0));
      }),
    }));
  } catch {
    return null;
  }
}

/** A slight preference for recognised places with parking on the spot over viewpoints reached on foot. */
const TRUST: Record<SiteKind, number> = { reserve: 1.1, park: 1.1, sanctuary: 1.1, urban: 0.9, 'country-park': 1.06, common: 1.02, forest: 1.02, viewpoint: 0.96 };
function trust(s: Site) {
  return TRUST[s.kind] * (s.within ? 1.04 : 1) * (s.walk && s.walk > 250 ? 0.97 : 1);
}

// ---------- The plan ----------
/** Whether Where to go covers a place: the UK (with Ireland, which comes with the same map data). */
export const coversPlace = (p: { lat: number; lon: number }) => (lands ? landOf(p) !== null : inUK(p.lat, p.lon));
void loadLands();

export async function planNight(home: Place, now: Date, maxMinutes: number, mags: Float32Array, homeSqm?: number | null): Promise<Plan> {
  const window = darkWindow(home, now);
  const homeB = homeSqm != null ? { sqm: homeSqm, bortle: bortleFromSqm(homeSqm) } : ((await skyBrightnessAt(home.lat, home.lon)) ?? { sqm: 18.9, bortle: 7 });
  const maxKm = Math.max(10, (maxMinutes - 5) / 1.3);
  const tz = home.timeZone;
  const localHour = (t: number) => Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hourCycle: 'h23', timeZone: tz }).format(t));
  const empty = (verdict: Plan['verdict']): Plan => ({
    window, home: { sqm: homeB.sqm, bortle: homeB.bortle, hours: [], best: null, clear: null }, results: [], moon: { lit: 0, upHours: 0 },
    verdict, forecast: false, searchedKm: Math.round(maxKm), bestBet: null,
  });
  if (!window) return empty({ tone: 'none', title: 'No proper darkness tonight', text: 'At this time of year the sky never gets fully dark here. The brightest stars and planets still show.' });
  const homeLand = landOf(home, await loadLands());
  if (homeLand === null) return empty({ tone: 'none', title: 'Where to go covers the UK', text: 'Suggestions of dark places to visit are available across the UK (and Ireland).' });

  // Hours to score: on the hour through the dark window (at most 12).
  const hours: number[] = [];
  for (let t = Math.ceil(window.start / 3600e3) * 3600e3; t <= window.end && hours.length < 12; t += 3600e3) hours.push(t);
  if (!hours.length) hours.push(window.start);
  const obs = new A.Observer(home.lat, home.lon, home.height);
  // The Sun and Moon each hour where Naked eye has them (true altitudes, no refraction).
  const sky = hours.map((t) => {
    const d = new Date(t);
    const s = A.Equator(A.Body.Sun, d, obs, true, true), m = A.Equator(A.Body.Moon, d, obs, true, true);
    const moon = A.Horizon(d, obs, m.ra, m.dec);
    return { t, sunAlt: A.Horizon(d, obs, s.ra, s.dec).altitude, moonAlt: moon.altitude, moonEnu: altAzToEnu(moon.altitude, moon.azimuth), moonMag: A.Illumination(A.Body.Moon, d).mag };
  });
  const lit = A.Illumination(A.Body.Moon, new Date(window.start)).phase_fraction;
  const moonUp = sky.filter((h) => h.moonAlt > 5).length;

  // Candidates: public places on the same landmass, noticeably darker than home, within reach, spread out.
  const cands = (await loadSites())
    .filter((s) => s.land === homeLand && s.sqm >= homeB.sqm + 0.35 && distanceKm(home, s) <= maxKm)
    .map((s) => ({ s, km: distanceKm(home, s) }))
    .sort((a, b) => b.s.sqm - 0.012 * b.km - (a.s.sqm - 0.012 * a.km));
  const picked: { s: Site; km: number }[] = [];
  for (const c of cands) {
    if (picked.some((p) => distanceKm(p.s, c.s) < 4)) continue;
    picked.push(c);
    if (picked.length >= 40) break;
  }

  // One forecast request for home and every candidate; hours outside the forecast count as unknown.
  const fc = await forecastFor([home, ...picked.map((p) => p.s)]);
  const cloudAt = (f: PointForecast | undefined, t: number) => {
    if (!f || !f.t.length || t < f.t[0] - 1800e3 || t > f.t[f.t.length - 1] + 1800e3) return null;
    const k = f.t.findIndex((x) => x >= t - 1800e3);
    return k < 0 ? null : f.cloud[k];
  };
  // Naked eye's sky at each place and hour (render/nakedeye.ts): its limit overhead, and the stars it shows across the
  // sky, fewer low down and near the Moon.
  const scoreHours = (sqm: number, f: PointForecast | undefined, arrive: number): HourScore[] =>
    sky.map((h) => {
      const cloud = cloudAt(f, h.t);
      const eye = eyeSkyFor(h.sunAlt, h.moonEnu, h.moonMag, 5, sqm, 0);
      const clear = cloud == null ? 0.6 : 1 - cloud / 100;
      return { t: h.t, cloud, nelm: eye.limit, stars: Math.round(starsSeen(eye, mags) * clear), ok: h.t + 3600e3 > arrive };
    });
  const summary = (all: HourScore[]) => {
    const hs = all.filter((h) => h.ok);
    if (!hs.length) return { best: null, clear: null, value: 0, from: 0, to: 0 };
    let best = hs[0];
    for (const h of hs) if (h.stars > best.stars) best = h;
    // Evening hours count fully; after 1 am (at the place) most people are home.
    let sum = 0, wsum = 0;
    for (const h of hs) {
      const local = localHour(h.t);
      const w = local >= 17 || local < 1 ? 1 : 0.55;
      sum += h.stars * w;
      wsum += w;
    }
    const withCloud = hs.filter((h) => h.cloud != null);
    const clear = withCloud.length ? Math.round(100 - withCloud.reduce((a, h) => a + h.cloud!, 0) / withCloud.length) : null;
    const k = hs.indexOf(best);
    let a = k, b = k;
    while (a > 0 && hs[a - 1].stars >= best.stars * 0.7) a--;
    while (b < hs.length - 1 && hs[b + 1].stars >= best.stars * 0.7) b++;
    return { best, clear, value: sum / wsum, from: Math.max(hs[a].t, window.start), to: Math.min(hs[b].t + 3600e3, window.end) };
  };
  const start = Math.max(now.getTime(), window.start - 3600e3);
  const homeHours = scoreHours(homeB.sqm, fc?.[0], now.getTime());
  const homeSum = summary(homeHours);
  const forecast = homeHours.some((h) => h.cloud != null);
  const evaluate = (s: Site, km: number, i: number): SiteResult => {
    const drive = estimateDrive(km);
    const hs = scoreHours(s.sqm, fc?.[i + 1], start + drive * 60e3);
    const sm = summary(hs);
    return {
      site: s, km, bearing: bearingDeg(home, s), hours: hs, clear: sm.clear,
      best: sm.best ?? { t: now.getTime(), cloud: null, nelm: 0, stars: 0, ok: false }, from: sm.from, to: sm.to,
      score: sm.value * Math.exp(-drive / 150) * trust(s),
    };
  };

  // The best six, kept varied (no two within 8 km of each other), then listed nearest first.
  const scored = picked.map(({ s, km }, i) => evaluate(s, km, i)).filter((r) => r.best.stars > 0).sort((a, b) => b.score - a.score);
  const top: SiteResult[] = [];
  for (const r of scored) {
    if (top.some((t) => distanceKm(t.site, r.site) < 8)) continue;
    top.push(r);
    if (top.length >= 6) break;
  }
  const results = [...top].sort((a, b) => a.km - b.km);

  // What to tell the user.
  const homeStars = homeSum.best?.stars ?? 0;
  const top1 = top[0];
  const anyClear = [homeSum.clear, ...results.map((r) => r.clear)].some((c) => c != null && c >= 30);
  let verdict: Plan['verdict'];
  if (!picked.length)
    verdict = homeSum.clear != null && homeSum.clear < 30
      ? { tone: 'cloudy', title: 'Cloudy here tonight', text: 'The forecast shows cloud over home, and no darker public places were found within reach.' }
      : { tone: 'stay', title: 'No darker places found nearby', text: 'No public places within reach are noticeably darker than where you are. A longer drive may help.' };
  else if (forecast && !anyClear) verdict = { tone: 'cloudy', title: 'Cloudy everywhere nearby', text: `The forecast shows cloud over home and every site within about ${maxMinutes} minutes tonight.` };
  else if (!top1) verdict = { tone: 'stay', title: 'Your sky is as good as it gets nearby', text: 'No public site within reach is much darker than where you are.' };
  else if (top1.best.stars < Math.max(homeStars * 1.35, homeStars + 150)) {
    const moony = lit > 0.6 && moonUp >= sky.length / 2;
    verdict = moony
      ? { tone: 'moon', title: 'The Moon evens things out tonight', text: `The Moon is ${Math.round(lit * 100)}% lit and up for most of the night, which brightens every sky, so a trip gains little. Moonless nights show the difference best.` }
      : { tone: 'stay', title: 'Not worth the drive tonight', text: `${top1.site.name} would show only a few more stars than home, given the cloud and darkness.` };
  } else {
    const ratio = top1.best.stars / Math.max(homeStars, 1);
    const moonNote = lit > 0.6 && moonUp >= sky.length / 2 ? ` The Moon is ${Math.round(lit * 100)}% lit, though, so the Milky Way will be faint: moonless nights are far better.` : '';
    verdict = {
      tone: 'go',
      title: `Best bet: ${top1.site.name}`,
      text: `${ratio >= 2 ? `About ${ratio >= 10 ? Math.round(ratio) : ratio.toFixed(1)} times as many stars as at home` : `Around ${(top1.best.stars - homeStars).toLocaleString('en-GB')} more stars than at home`}, about ${Math.round(top1.km)} km away.${moonNote}`,
    };
  }

  return {
    window,
    home: { sqm: homeB.sqm, bortle: homeB.bortle, hours: homeHours, best: homeSum.best, clear: homeSum.clear },
    results,
    moon: { lit, upHours: moonUp },
    verdict,
    forecast,
    searchedKm: Math.round(maxKm),
    bestBet: verdict.tone === 'go' ? top1.site.id : null,
  };
}

// ---------- Directions ----------
const isApple = () => /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);
/** Driving directions to the place (its car park, where it has one): Apple Maps on Apple devices, Google Maps elsewhere. */
export function directionsLink(s: Site): { label: string; href: string } {
  const [lat, lon] = s.park ?? [s.lat, s.lon];
  const c = `${lat.toFixed(5)},${lon.toFixed(5)}`;
  return isApple()
    ? { label: 'Apple Maps', href: `https://maps.apple.com/?daddr=${c}&dirflg=d` }
    : { label: 'Google Maps', href: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(c)}&travelmode=driving` };
}
